// Parking Lot Panel Component
import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, AlertCircle, CheckCircle, RotateCcw, 
  MoreVertical, Plus, Trash2, ClipboardList, FastForward,
  Check, Loader2
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const ParkingLotPanel = ({ 
  meetingId, 
  agendaItems, 
  isOpen, 
  onClose, 
  isLive,
  endpointBase = '/board',
  allowConvertToAction = true
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ title: '', description: '', source_agenda_item_id: '' });

  useEffect(() => {
    if (isOpen && meetingId) {
      fetchItems();
    }
  }, [isOpen, meetingId]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${endpointBase}/meetings/${meetingId}/parking-lot`);
      setItems(res.data.items || []);
    } catch (err) {
      console.error('Failed to fetch parking lot:', err);
    } finally {
      setLoading(false);
    }
  };

  const addItem = async () => {
    if (!newItem.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    try {
      await api.post(`${endpointBase}/meetings/${meetingId}/parking-lot`, {
        ...newItem,
        source_agenda_item_id: newItem.source_agenda_item_id || null
      });
      toast.success('Added to parking lot');
      setNewItem({ title: '', description: '', source_agenda_item_id: '' });
      setShowAddForm(false);
      fetchItems();
    } catch (err) {
      toast.error('Failed to add item');
    }
  };

  const updateStatus = async (itemId, newStatus) => {
    try {
      await api.put(`${endpointBase}/meetings/${meetingId}/parking-lot/${itemId}`, { status: newStatus });
      toast.success(`Marked as ${newStatus.replace('_', ' ')}`);
      fetchItems();
    } catch (err) {
      toast.error('Failed to update item');
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Delete this parking lot item?')) return;
    
    try {
      await api.delete(`${endpointBase}/meetings/${meetingId}/parking-lot/${itemId}`);
      toast.success('Item deleted');
      fetchItems();
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  const convertToAction = async (itemId) => {
    if (!allowConvertToAction) return;
    try {
      await api.post(`${endpointBase}/meetings/${meetingId}/parking-lot/${itemId}/convert-to-action`);
      toast.success('Converted to action item');
      fetchItems();
    } catch (err) {
      toast.error('Failed to convert');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      parked: 'bg-yellow-100 text-yellow-700',
      addressed: 'bg-green-100 text-green-700',
      deferred_to_next: 'bg-blue-100 text-blue-700',
      converted_to_action: 'bg-purple-100 text-purple-700'
    };
    const labels = {
      parked: 'Parked',
      addressed: 'Addressed',
      deferred_to_next: 'Deferred',
      converted_to_action: 'Action Item'
    };
    return <Badge className={styles[status] || styles.parked}>{labels[status] || status}</Badge>;
  };

  const parkedItems = items.filter(i => i.status === 'parked');
  const resolvedItems = items.filter(i => i.status !== 'parked');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-purple-600" />
            Parking Lot
            <Badge variant="outline" className="ml-2">{items.length} items</Badge>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Add Item Form */}
            {showAddForm ? (
              <div className="bg-yellow-50 rounded-lg p-4 mb-4 space-y-3">
                <h4 className="font-medium text-yellow-900">Add to Parking Lot</h4>
                <div className="space-y-3">
                  <div>
                    <Label>Topic *</Label>
                    <Input
                      value={newItem.title}
                      onChange={(e) => setNewItem(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="What needs to be discussed later?"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={newItem.description}
                      onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Additional context..."
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Came from agenda item</Label>
                    <Select 
                      value={newItem.source_agenda_item_id || 'none'}
                      onValueChange={(v) => setNewItem(prev => ({ ...prev, source_agenda_item_id: v === 'none' ? '' : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Optional" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {agendaItems.map(item => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.sort_order}. {item.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={addItem}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Item
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mb-4">
                <Button variant="outline" onClick={() => setShowAddForm(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add to Parking Lot
                </Button>
              </div>
            )}

            {/* Items List */}
            <div className="flex-1 overflow-y-auto space-y-4">
              {/* Parked Items */}
              {parkedItems.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                    Parked Items ({parkedItems.length})
                  </h4>
                  <div className="space-y-2">
                    {parkedItems.map(item => (
                      <div key={item.id} className="border border-yellow-200 bg-yellow-50 rounded-lg p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{item.title}</p>
                            {item.description && (
                              <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                            )}
                            <div className="flex gap-3 mt-2 text-xs text-gray-500">
                              {item.raised_by_name && <span>By: {item.raised_by_name}</span>}
                              {item.source_agenda_title && (
                                <span>From: {item.agenda_sort_order}. {item.source_agenda_title}</span>
                              )}
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => updateStatus(item.id, 'addressed')}>
                                <Check className="h-4 w-4 mr-2 text-green-600" />
                                Mark Addressed
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => updateStatus(item.id, 'deferred_to_next')}>
                                <FastForward className="h-4 w-4 mr-2 text-blue-600" />
                                Defer to Next Meeting
                              </DropdownMenuItem>
                              {allowConvertToAction && (
                                <DropdownMenuItem onClick={() => convertToAction(item.id)}>
                                  <ClipboardList className="h-4 w-4 mr-2 text-purple-600" />
                                  Convert to Action Item
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => deleteItem(item.id)} className="text-red-600">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Resolved Items */}
              {resolvedItems.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Resolved ({resolvedItems.length})
                  </h4>
                  <div className="space-y-2">
                    {resolvedItems.map(item => (
                      <div key={item.id} className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-700">{item.title}</p>
                              {getStatusBadge(item.status)}
                            </div>
                            {item.resolved_notes && (
                              <p className="text-sm text-gray-500 mt-1">{item.resolved_notes}</p>
                            )}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => updateStatus(item.id, 'parked')}
                            title="Move back to parked"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {items.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No items in the parking lot</p>
                  <p className="text-sm mt-1">Add topics that come up but need to be discussed later</p>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ParkingLotPanel;
